import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AssignmentQuestion } from '../../../../models/assignments';

// Чистая функция переноса вопроса в новом порядке.
// Возвращает null, если activeId/overId не найдены среди questions или совпадают —
// вызывающий код (handleDragEnd) при null просто ничего не делает.
export function moveQuestion(
  questions: AssignmentQuestion[],
  activeId: string,
  overId: string,
): AssignmentQuestion[] | null {
  if (activeId === overId) {
    return null;
  }

  const oldIndex = questions.findIndex((q) => q.id === activeId);
  const newIndex = questions.findIndex((q) => q.id === overId);

  if (oldIndex === -1 || newIndex === -1) {
    return null;
  }

  return arrayMove(questions, oldIndex, newIndex);
}

interface SortableQuestionRowProps {
  question: AssignmentQuestion;
  disabled?: boolean;
  renderQuestion: (_q: AssignmentQuestion) => React.ReactNode;
}

const SortableQuestionRow: React.FC<SortableQuestionRowProps> = ({
  question,
  disabled,
  renderQuestion,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id, disabled });

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={rowStyle}>
      <span
        {...attributes}
        {...(disabled ? {} : listeners)}
        aria-label="Перетащить"
        role="button"
        tabIndex={0}
        style={{
          cursor: disabled ? 'default' : 'grab',
          opacity: disabled ? 0.3 : 1,
          display: 'inline-flex',
        }}
      >
        <svg
          width="16"
          height="22"
          viewBox="0 0 16 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="5" cy="4" r="1.5" fill="currentColor" />
          <circle cx="11" cy="4" r="1.5" fill="currentColor" />
          <circle cx="5" cy="11" r="1.5" fill="currentColor" />
          <circle cx="11" cy="11" r="1.5" fill="currentColor" />
          <circle cx="5" cy="18" r="1.5" fill="currentColor" />
          <circle cx="11" cy="18" r="1.5" fill="currentColor" />
        </svg>
      </span>

      <div style={{ flex: 1 }}>{renderQuestion(question)}</div>
    </div>
  );
};

export interface SortableQuestionListProps {
  questions: AssignmentQuestion[];
  disabled?: boolean;
  onReorder: (_ids: string[]) => Promise<void>;
  renderQuestion: (_q: AssignmentQuestion) => React.ReactNode;
}

// Сортируемый список вопросов задания. Порядок хранит родитель:
// компонент не держит своё состояние и не делает откат сам —
// при reject промиса onReorder родитель восстанавливает прежний порядок.
export const SortableQuestionList: React.FC<SortableQuestionListProps> = ({
  questions,
  disabled,
  onReorder,
  renderQuestion,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      return;
    }

    const next = moveQuestion(questions, String(active.id), String(over.id));
    if (next) {
      void onReorder(next.map((q) => q.id));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={questions.map((q) => q.id)}
        strategy={verticalListSortingStrategy}
      >
        <div>
          {questions.map((question) => (
            <SortableQuestionRow
              key={question.id}
              question={question}
              disabled={disabled}
              renderQuestion={renderQuestion}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableQuestionList;
